import { useImmer } from "use-immer";
import moment from "moment";
import {
  useGetAttendanceDateQuery,
  useGetBatchValidityDateQuery,
  useGetProgramForAttendanceQuery,
} from ".";
import { useServerTime } from "modules/LearningMaterials";
import { useEffect, useMemo } from "react";

export const useAttendance = ({ load = false }) => {
  const [activeStartDate, setActiveStartDate] = useImmer(null);
  const [attendanceProgramSelected, setAttendanceProgramSelected] =
    useImmer(null);
  const month = useMemo(() => {
    return activeStartDate ? moment(activeStartDate).get("month") + 1 : 0;
  }, [activeStartDate]);
  const year = useMemo(() => {
    return activeStartDate ? moment(activeStartDate).get("year") : 2025;
  }, [activeStartDate]);

  const { data: attendanceProgram, isLoading: attendanceProgramLoading } =
    useGetProgramForAttendanceQuery(load);

  const { data: validityDates, isLoading: validityLoading } =
    useGetBatchValidityDateQuery({ attendanceProgramSelected });

  const { data: attendanceDates, isLoading: datesLoading } =
    useGetAttendanceDateQuery({ attendanceProgramSelected, month, year });

  const { data } = useServerTime({ load: load && !activeStartDate });

  useEffect(() => {
    if (data) {
      const trimmed = data.slice(0, 23);
      const date = new Date(trimmed);
      setActiveStartDate(date);
    }
  }, [data]);

  const programList = useMemo(() => {
    return attendanceProgram
      ? attendanceProgram?.map((item) => ({
          label: item.text,
          value: item.value,
        }))
      : [];
  }, [attendanceProgram]);

  const checkDayHighlight = (date, view) => {
    const key = moment(date)?.format("YYYY-MM-DD");
    const checkDate = moment().format("YYYY-MM-DD");
    const { startDate, endDate } = validityDates || {};
    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : checkDate;
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : checkDate;
    const validity = moment(key).isBetween(
      formattedStartDate,
      formattedEndDate,
      null,
      []
    );

    const dayHighlight =
      view === "month" &&
      date?.getMonth() === activeStartDate?.getMonth() &&
      attendanceDates &&
      validity;

    return { dayHighlight, key, checkDate };
  };

  const tileDisabled = (date, view) =>
    view === "month" &&
    date &&
    activeStartDate &&
    date.getMonth() !== activeStartDate.getMonth();

  const showMarker = (date, view) => {
    const { dayHighlight, key, checkDate } = checkDayHighlight(date, view);
    const serverDate = data ? moment(data).format("YYYY-MM-DD") : checkDate;
    if (dayHighlight) {
      if (key < serverDate && attendanceDates) {
        return (
          <div
            style={{
              height: "8px",
              width: "8px",
              borderRadius: "50%",
              backgroundColor: attendanceDates.includes(key)
                ? "#238A24"
                : "#CB4848",
              position: "absolute",
              left: "50%",
              marginLeft: "-4px",
              bottom: " 7px",
              zIndex: 10,
            }}
          ></div>
        );
      }
    }
    return null;
  };

  const getClassess = (date, view) => {
    const { dayHighlight, key, checkDate } = checkDayHighlight(date, view);
    const serverDate = data ? moment(data).format("YYYY-MM-DD") : checkDate;
    if (dayHighlight) {
      if (key === serverDate) {
        return attendanceDates.includes(key) ? "attended-mark" : "default-mark";
      }
    } else {
      return attendanceProgramSelected ? "dim-other" : "";
    }
  };

  const onProgramChange = (e) => {
    setAttendanceProgramSelected((draft) => {
      draft = e?.value;
      return draft;
    });
  };

  return {
    activeStartDate,
    setActiveStartDate,
    attendanceProgramSelected,
    setAttendanceProgramSelected,
    attendanceProgram,
    attendanceProgramLoading,
    attendanceDates,
    datesLoading,
    showMarker,
    getClassess,
    programList,
    onProgramChange,
    validityLoading,
    tileDisabled,
  };
};
